import { useEffect, useState } from 'react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@nesso/ui/primitives/dialog';

import type { TreatmentApproach } from '@/entities/session';
import {
    treatmentApproachesGroupsOrder,
    treatmentApproachesOptions,
} from '@/entities/treatment-approach';
import { InlineMultiSelect } from '@/shared/ui/common/inline-multi-select';
import { MultiSelect } from '@/shared/ui/common/multi-select';

type Props = {
    open: boolean;
    existingApproaches: TreatmentApproach[];
    onOpenChange: (open: boolean) => void;
    onAdd: (approaches: TreatmentApproach[]) => void;
};

export function NessoAddApproachDialog({ open, existingApproaches, onOpenChange, onAdd }: Props) {
    const [selected, setSelected] = useState<string[]>([]);
    const isXs = useBreakpointDown('xs');

    useEffect(() => {
        if (!open) setSelected([]);
    }, [open]);

    const availableOptions = treatmentApproachesOptions.filter(
        (option) => !existingApproaches.includes(option.value),
    );

    const handleAdd = () => {
        onAdd(selected as TreatmentApproach[]);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent fullscreen={isXs} className="lg:max-w-md">
                <DialogHeader className={cn(isXs && 'text-left px-6 pt-6 pb-3')}>
                    <DialogTitle className="text-lg font-semibold sm:text-base sm:font-normal">
                        Add treatment approach
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col h-full overflow-hidden">
                    {isXs ? (
                        <InlineMultiSelect
                            options={availableOptions}
                            selected={selected}
                            onChange={setSelected}
                            searchPlaceholder="Search approaches"
                            groupOrder={treatmentApproachesGroupsOrder}
                        />
                    ) : (
                        <MultiSelect
                            options={availableOptions}
                            selected={selected}
                            onChange={setSelected}
                            placeholder=""
                            groupLabel="Select approaches to add to your note"
                            searchPlaceholder="Search approaches"
                            className="w-full min-h-15"
                        />
                    )}
                </div>
                <DialogFooter className={cn(isXs && 'flex-row gap-2 p-6')}>
                    {isXs ? (
                        <>
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={selected.length === 0}
                                className="flex-1"
                                onClick={handleAdd}
                            >
                                Add
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleAdd} disabled={selected.length === 0}>
                                Add
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
